import * as React from 'react'

import { observer } from 'mobx-react'

import { WorkflowDurationType } from '~/client/graph'
import WarningNote, {
  IWarningNote,
  WarningNoteSummary,
} from '~/client/src/shared/components/WarningNote/WarningNote'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { cn } from '~/client/src/shared/utils/cn'
import { getDeadlineOptionText } from '~/client/src/shared/utils/DeadlineOptions'

import { getLateNote, getTooLongNote } from '../hoursOfOperationWarning'
import SitePermitCreationFormStore from '../WorkflowCreationForm.store'

interface IProps {
  store: SitePermitCreationFormStore
  locationIds: string[]
  isViewMode?: boolean
  className?: string
}

// getTitle names resources the store can't, such as materials, the way the
// field's chips do.
const getResourceNotes = (
  store: SitePermitCreationFormStore,
  locationIds: string[],
  getTitle?: (locationId: string) => string,
): IWarningNote[] =>
  Array.from(new Set(locationIds.filter(Boolean)))
    .map(id => {
      const note = store.getResourceNote(id)
      return note && getTitle ? { ...note, title: getTitle(id) } : note
    })
    .filter(Boolean)

// The field already names a single value, so a note names its resource only
// when the field holds several.
const shouldNameResources = (locationIds: string[]): boolean =>
  new Set(locationIds.filter(Boolean)).size > 1

// Outside project hours only blocks when the type says so (and never for an
// admin or forms master). Otherwise it is a warning, stated as one.
export const doesNonWorkTimeBlock = (
  store: SitePermitCreationFormStore,
): boolean => store.shouldBlockOnNonWorkTimes && !store.isAdminOrFormsMaster

const getProjectHoursNote = (
  store: SitePermitCreationFormStore,
): IWarningNote | null => {
  const { projectHoursNote } = store
  if (!projectHoursNote || !doesNonWorkTimeBlock(store)) return projectHoursNote
  return { ...projectHoursNote, tone: 'blocked' }
}

const getDatesTooLongNote = (
  store: SitePermitCreationFormStore,
): IWarningNote | null => {
  const { startDate, endDate } = store.editablePermit
  const { areDatesWithinMaxDuration, maxDurationLabel, formTypeMaxDuration } =
    store.workflowFieldsStore
  if (!startDate || !endDate || areDatesWithinMaxDuration) return null

  const { limitIsOneWorkingDay, limitIsOneWorkWeek, limitIsX } =
    Localization.translator
  switch (formTypeMaxDuration.maxDurationType) {
    case WorkflowDurationType.AllDay:
      return getTooLongNote(limitIsOneWorkingDay, endDate - startDate)
    case WorkflowDurationType.WorkWeek:
      return getTooLongNote(limitIsOneWorkWeek, endDate - startDate)
    default:
      return getTooLongNote(limitIsX(maxDurationLabel), endDate - startDate)
  }
}

const getLateRequestNote = (
  store: SitePermitCreationFormStore,
): IWarningNote | null => {
  const {
    isMarkedAsLateRequest,
    bookingDeadlineInterval,
    editablePermit,
    shouldBlockLateRequesting,
  } = store
  if (!isMarkedAsLateRequest) return null

  return getLateNote(
    editablePermit?.id ? null : getDeadlineOptionText(bookingDeadlineInterval),
    !!shouldBlockLateRequesting,
  )
}

// One note for each thing wrong with the dates: outside project hours, longer
// than the type allows, asked for too late. Red where it stops the booking.
export const getDateNotes = (
  store: SitePermitCreationFormStore,
  isViewMode?: boolean,
): IWarningNote[] =>
  [
    getProjectHoursNote(store),
    isViewMode ? null : getDatesTooLongNote(store),
    getLateRequestNote(store),
  ].filter(Boolean)

export const HoursOfOperationNoteList: React.FC<{
  notes: IWarningNote[]
  isTitleShown?: boolean
  isSummarized?: boolean
  isViewMode?: boolean
  className?: string
}> = ({ notes, isTitleShown, isSummarized, isViewMode, className }) => {
  if (!notes.length) return null

  return (
    <div
      className={cn(
        'tw:flex tw:flex-col',
        isTitleShown ? 'tw:gap-2' : 'tw:gap-1',
        isViewMode && 'tw:mt-1',
        className,
      )}
    >
      {isSummarized ? (
        <WarningNoteSummary notes={notes} />
      ) : (
        notes.map((note, index) => (
          <WarningNote key={index} note={note} isTitleShown={isTitleShown} />
        ))
      )}
    </div>
  )
}

// For an input's note slot, which takes nothing when no resource has a note.
export const renderResourceNote = (
  store: SitePermitCreationFormStore,
  locationIds: string[],
  getTitle?: (locationId: string) => string,
): JSX.Element => {
  const notes = getResourceNotes(store, locationIds, getTitle)
  return notes.length ? (
    <HoursOfOperationNoteList
      notes={notes}
      isTitleShown={shouldNameResources(locationIds)}
    />
  ) : null
}

const HoursOfOperationNote: React.FC<IProps> = observer(
  ({ store, locationIds, isViewMode, className }) => (
    <HoursOfOperationNoteList
      notes={getResourceNotes(store, locationIds)}
      isTitleShown={shouldNameResources(locationIds)}
      isViewMode={isViewMode}
      className={className}
    />
  ),
)

export default HoursOfOperationNote
